import type { InjectionKey } from 'vue'
import type { ChartSeries, InstantRangeValue, RollingSpan } from '@hantera/design-system'

import type { DateRange } from './traces-view-context'

/**
 * Which sections the caller may read. Each is gated on the permission its endpoint actually checks,
 * rather than on the family's `:read` — the view this replaces got that wrong and 403'd on every
 * request for a read-only role.
 */
export interface StatisticsSections {
  ingress: boolean
  egress: boolean
  jobs: boolean
}

export interface IngressRow {
  ingressId: string
  requests: number
  clientErrors: number
  serverErrors: number
  p95DurationMs: number | null
  maxDurationMs: number | null
}

export interface EgressRow {
  egressKey: string
  total: number
  cacheHits: number
  failed: number
  p95DurationMs: number | null
  maxDurationMs: number | null
}

export interface HostRow {
  host: string
  transport: string
  total: number
  failed: number
  p95DurationMs: number | null
  maxDurationMs: number | null
}

export interface JobDefinitionRow {
  jobDefinitionId: string
  due: number
  scheduled: number
  successful: number
  failed: number
  p95ExecutionMs: number | null
  maxExecutionMs: number | null
}

export interface QueueRow {
  queue: string
  readyDepth: number
  dueDepth: number
  busyWorkers: number
  workers: number
  /** Null rather than zero when the pool has no workers: 0/0 is undefined, not idle. */
  saturation: number | null
  completed: number
  failed: number
}

/**
 * The Statistics tab's view state: one date range over all four sources, and the series and rows
 * derived from them.
 *
 * A view context rather than a Pinia store because the date range is per-view — two Monitoring views
 * must be able to look at two different windows at once.
 */
export interface StatisticsViewContext {
  readonly sections: StatisticsSections

  readonly dateRange: DateRange

  /**
   * The span the window was derived from, when it ends at "now". Null for a fixed span.
   *
   * The range itself is always concrete; this is what lets the tab slide it forward rather than
   * re-poll an increasingly stale window.
   */
  readonly rolling: RollingSpan | null

  get loadState(): Readonly<{
    isLoading: boolean
    error: string | null
    /** When the last successful fetch landed, so the toolbar can say how stale the charts are. */
    lastRefreshedAt: Date | null
  }>


  readonly ingressRequestSeries: ChartSeries[]
  readonly ingressLatencySeries: ChartSeries[]
  readonly egressCallSeries: ChartSeries[]
  readonly egressLatencySeries: ChartSeries[]
  readonly hostCallSeries: ChartSeries[]
  readonly hostLatencySeries: ChartSeries[]
  readonly jobOutcomeSeries: ChartSeries[]
  readonly jobLatencySeries: ChartSeries[]
  readonly queueDepthSeries: ChartSeries[]

  readonly ingressRows: IngressRow[]
  readonly egressRows: EgressRow[]
  readonly hostRows: HostRow[]
  readonly jobRows: JobDefinitionRow[]
  readonly queueRows: QueueRow[]

  refresh(): Promise<void>

  /**
   * Advances a rolling window to end at now, then refreshes. A no-op beyond the refresh when the
   * range is fixed, which is what lets one timer serve both modes.
   */
  tick(): Promise<void>

  setRange(range: InstantRangeValue): void

  /** Live-event handlers, one per subscription the tab opens. */
  handleIngressEvent(data: any): void
  handleEgressEvent(data: any): void
  handleHostEvent(data: any): void
  handleJobEvent(data: any): void
  handleQueueEvent(data: any): void
}

export const statisticsViewContextKey: InjectionKey<StatisticsViewContext> =
  Symbol('statisticsViewContext')
